import * as React from 'react';
import { toast } from 'sonner';
import {
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Printer,
  Table2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { downloadFromApi } from '@/lib/download';
import { cn } from '@/lib/utils';

const FORMATS = {
  pdf: { label: 'PDF', icon: FileText, type: 'application/pdf' },
  xlsx: {
    label: 'Excel',
    icon: Table2,
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  csv: { label: 'CSV', icon: FileSpreadsheet, type: 'text/csv;charset=utf-8' },
};

const slugify = (value) =>
  String(value || 'export')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'export';

/**
 * Menu d'export réutilisable.
 *
 * Chaque entrée décrit un document : `{ label, url, params, formats }`.
 *
 * ## Convention d'URL (tranchée)
 *
 * **Une URL par document, le format en paramètre `format`** (`pdf | xlsx | csv`).
 * Un document est une chose — « les camions en trajet » — et le format n'est
 * qu'une façon de le rendre : le mettre dans le chemin créerait trois routes à
 * garder synchronisées pour une seule question métier, et c'est exactement ainsi
 * que le PDF et l'Excel finissent par ne plus dire la même chose. C'est la
 * convention de `/print/*`, et celle que toute nouvelle impression doit suivre.
 *
 * `urls: { pdf: '/…' }` reste accepté comme **échappatoire** pour les endpoints
 * hérités dont le chemin porte déjà l'extension (facture PDF, bon de livraison).
 * Dans ce cas le paramètre `format` n'est pas envoyé, l'URL le dit déjà.
 * À n'utiliser que pour l'existant : rien de neuf ne devrait en avoir besoin.
 */
const PrintMenu = ({
  items = [],
  label = 'Exporter',
  align = 'end',
  variant = 'outline',
  size = 'sm',
  className,
  disabled = false,
}) => {
  const [pending, setPending] = React.useState(null);

  const usableItems = items.filter((item) => item && (item.url || item.urls));
  if (usableItems.length === 0) return null;

  const run = async (item, format) => {
    const key = `${item.label}-${format}`;
    setPending(key);
    try {
      const url = item.urls?.[format] || item.url;
      const params = item.urls?.[format]
        ? { ...(item.params || {}) }
        : { ...(item.params || {}), format };
      await downloadFromApi(
        url,
        params,
        `${slugify(item.filename || item.label)}.${format}`,
        FORMATS[format]?.type,
      );
    } catch (error) {
      toast.error(`Export ${FORMATS[format]?.label || format} indisponible`);
    } finally {
      setPending(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={cn('gap-2', className)}
          disabled={disabled}
        >
          {pending ? (
            <Loader2 className="animate-spin" aria-hidden="true" />
          ) : (
            <Printer aria-hidden="true" />
          )}
          {label}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align={align} className="min-w-56">
        {usableItems.map((item, index) => {
          const formats = (item.formats || ['pdf']).filter((f) => FORMATS[f]);
          return (
            <React.Fragment key={item.label || index}>
              {index > 0 && <DropdownMenuSeparator />}
              {usableItems.length > 1 && (
                <DropdownMenuLabel className="text-xs font-black uppercase tracking-wide text-muted-foreground">
                  {item.label}
                </DropdownMenuLabel>
              )}
              {formats.map((format) => {
                const Icon = FORMATS[format].icon;
                const key = `${item.label}-${format}`;
                return (
                  <DropdownMenuItem
                    key={format}
                    disabled={pending === key}
                    onSelect={(event) => {
                      event.preventDefault();
                      run(item, format);
                    }}
                  >
                    {pending === key ? (
                      <Loader2 className="animate-spin" aria-hidden="true" />
                    ) : (
                      <Icon aria-hidden="true" />
                    )}
                    <span>
                      {usableItems.length > 1
                        ? FORMATS[format].label
                        : `${item.label} · ${FORMATS[format].label}`}
                    </span>
                    <DropdownMenuShortcut>
                      <Download className="size-3.5" aria-hidden="true" />
                    </DropdownMenuShortcut>
                  </DropdownMenuItem>
                );
              })}
            </React.Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
PrintMenu.displayName = 'PrintMenu';

export { PrintMenu };
export default PrintMenu;
